/**
 * Display formatting.
 *
 * All amounts arriving from the API are base-unit decimal strings. Converting
 * them with `Number()` would silently lose precision on anything with 18
 * decimals, so the integer/fraction split is done on the string and only the
 * grouping is delegated to `Intl`.
 */

/** Base units → exact decimal string. Never rounds. */
export function formatUnits(value: string, decimals: number): string {
  if (!value) return '0';
  const negative = value.startsWith('-');
  const digits = (negative ? value.slice(1) : value).padStart(decimals + 1, '0');
  const whole = digits.slice(0, digits.length - decimals);
  const frac = decimals === 0 ? '' : digits.slice(digits.length - decimals).replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${frac ? `.${frac}` : ''}`;
}

/**
 * A crypto amount, grouped and capped for display.
 *
 * Significant-figure behaviour matters here: showing "0.00" for a real holding
 * of 0.000004 BTC looks like the money is gone. So tiny amounts keep enough
 * decimals to stay non-zero.
 */
export function formatAmount(value: string, decimals: number, maxDecimals?: number): string {
  const exact = formatUnits(value, decimals);
  const negative = exact.startsWith('-');
  const [wholeRaw = '0', fracRaw = ''] = (negative ? exact.slice(1) : exact).split('.');

  let cap = maxDecimals ?? (wholeRaw === '0' ? 8 : 6);
  if (wholeRaw !== '0' && Number(wholeRaw) >= 1000) cap = 2;

  let frac = fracRaw.slice(0, cap).replace(/0+$/, '');

  // If truncation would render a non-zero balance as zero, keep going until a
  // significant digit appears.
  if (wholeRaw === '0' && frac === '' && fracRaw !== '') {
    const firstSignificant = fracRaw.search(/[1-9]/);
    if (firstSignificant >= 0) frac = fracRaw.slice(0, firstSignificant + 3).replace(/0+$/, '');
  }

  const grouped = new Intl.NumberFormat('en-US').format(BigInt(wholeRaw));
  return `${negative ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`;
}

/** Fiat cents → localised currency string. */
export function formatFiat(cents: string, currency = 'usd', compact = false): string {
  const amount = Number(cents) / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      ...(compact && Math.abs(amount) >= 10_000
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    }).format(amount);
  } catch {
    // Non-ISO quote currencies (btc, eth) are not valid Intl currency codes.
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/** A signed percentage, e.g. `"+2.41%"`. */
export function formatPercent(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;
}

/**
 * Shorten an address for a dense list.
 *
 * Both ends are kept: address-poisoning attacks work by generating an address
 * with the same first and last few characters as one you have used before, so a
 * middle-elided form is only a glance, never a verification. Any screen where a
 * user confirms a destination shows the address in full.
 */
export function shortenAddress(address: string, lead = 8, tail = 6): string {
  if (address.length <= lead + tail + 1) return address;
  return `${address.slice(0, lead)}…${address.slice(-tail)}`;
}

/** Relative time, e.g. "3 minutes ago". */
export function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 45) return 'just now';

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
  ];

  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  for (const [unit, secondsPer] of units) {
    if (seconds >= secondsPer) return formatter.format(-Math.floor(seconds / secondsPer), unit);
  }
  return 'just now';
}

/** Absolute date, for transaction lists. */
export function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

/** Seconds → "12m 30s", for the auto-lock countdown. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** A stable colour per chain, for avatars and chart series. */
export function chainColor(chainId: string): string {
  const known: Record<string, string> = {
    bitcoin: '#f7931a',
    'bitcoin-testnet': '#8a8a8a',
    ethereum: '#8a92b2',
    litecoin: '#a5a8a9',
    dogecoin: '#c3a634',
    'bitcoin-cash': '#8dc351',
    dash: '#008ce7',
    solana: '#14f195',
    polygon: '#8247e5',
    arbitrum: '#28a0f0',
    optimism: '#ff0420',
    base: '#0052ff',
    bsc: '#f3ba2f',
    avalanche: '#e84142',
    cosmos: '#5064fb',
    tron: '#eb0029',
    xrp: '#23292f',
    monero: '#ff6600',
    zcash: '#ecb244',
    ravencoin: '#384182',
  };
  if (known[chainId]) return known[chainId] as string;

  // Deterministic hue from the id, so unknown chains still get a stable colour.
  let hash = 0;
  for (let i = 0; i < chainId.length; i++) hash = (hash * 31 + chainId.charCodeAt(i)) | 0;
  return `hsl(${Math.abs(hash) % 360} 55% 55%)`;
}

/** Join class names, dropping falsy entries. */
export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}
